import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { clearOldSessions } from '../lib/redux/sessions/data/data-slice';

interface UseSessionMemoryManagerProps {
  currentSessionIds: string[];
  maxSessions?: number;
  cleanupInterval?: number; // in milliseconds
}

/**
 * Hook to manage memory by cleaning up old session data from Redux store
 * This prevents OOM errors when users browse sessions for extended periods
 */
export const useSessionMemoryManager = ({
  currentSessionIds,
  maxSessions = 10,
  cleanupInterval = 60000 // 1 minute
}: UseSessionMemoryManagerProps) => {
  const dispatch = useDispatch();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastCleanupRef = useRef<number>(Date.now());

  useEffect(() => {
    // Function to perform cleanup
    const performCleanup = () => {
      const now = Date.now();
      // Only cleanup if enough time has passed
      if (now - lastCleanupRef.current >= cleanupInterval) {
        dispatch(clearOldSessions({ 
          keepSessionIds: currentSessionIds,
          maxSessions 
        }));
        lastCleanupRef.current = now;
      }
    };

    // Perform initial cleanup
    performCleanup();

    // Set up interval for periodic cleanup
    intervalRef.current = setInterval(performCleanup, cleanupInterval);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [currentSessionIds, maxSessions, cleanupInterval, dispatch]);

  // Also perform cleanup when currentSessionIds change significantly
  useEffect(() => {
    const now = Date.now();
    // If sessions have changed and at least 10 seconds have passed, do a cleanup
    if (now - lastCleanupRef.current >= 10000) {
      dispatch(clearOldSessions({ 
        keepSessionIds: currentSessionIds,
        maxSessions 
      }));
      lastCleanupRef.current = now;
    }
  }, [currentSessionIds, maxSessions, dispatch]);
};